import { action } from '@reatom/framework'
import i18next from 'i18next'

import {
  AuthErrorCode,
  FastifyErrorCode,
  PremiumErrorCode,
  ServicesErrorCode,
  SwapErrorCode,
  TradeErrorCode,
  UserErrorCode,
  WalletErrorCode,
} from '@root/shared/api/api-error-code'
import { ReferralErrorCode } from '@root/shared/api/api-error-code'
import { SecureStorageErrorCode } from '@root/shared/ui/secure-storage-error-code'

import { ApiError } from '../api'
import { showSnackbarAction } from '../ui-kit/snackbar/atoms'

import type { UnknownError } from '../core/Error/Error'
import type { TransKey } from '@root/shared/localization'

export type SupportedErrorCode =
  | SwapErrorCode
  | WalletErrorCode
  | ServicesErrorCode
  | PremiumErrorCode
  | AuthErrorCode
  | TradeErrorCode
  | FastifyErrorCode
  | UserErrorCode
  | ReferralErrorCode
  | SecureStorageErrorCode

export const showErrorSnackbarAction = action(
  (
    ctx,
    error: ApiError.ApiError | UnknownError,
    args?: Record<string, string>,
  ) => {
    const errorCode = guessErrorCode(error)

    showErrorCodeSnackbarAction(ctx, errorCode, args)
  },
  'showErrorSnackbarAction',
)

export const showErrorCodeSnackbarAction = action(
  (
    ctx,
    errorCode: SupportedErrorCode | undefined,
    args?: Record<string, string>,
  ) => {
    if (errorCode) {
      const translationKey =
        ERROR_TRANSLATION_MAP[errorCode as SupportedErrorCode]

      showSnackbarAction(ctx, {
        intent: 'error',
        text: i18next.t('common.something-went-wrong'),
        description: i18next.t(translationKey, { args }).toString(),
      })

      return
    }

    showSnackbarAction(ctx, {
      intent: 'error',
      text: i18next.t('common.something-went-wrong', { args }),
    })
    return
  },
  'showErrorCodeSnackbarAction',
)

const guessErrorCode = (
  error: ApiError.ApiError | UnknownError,
): SupportedErrorCode | undefined => {
  if (ApiError.is(error) && error.code) {
    return error.code as SupportedErrorCode
  }

  return undefined
}

const ERROR_TRANSLATION_MAP: Record<SupportedErrorCode, TransKey> = {
  [ReferralErrorCode.ReferralCodeAlreadyExists]:
    'error.referral.this-referral-code-already-exists',
  [SwapErrorCode.InsufficientBalance]: 'error.position.insufficient-balance',
  [SwapErrorCode.InsufficientReserves]: 'error.position.insufficient-reserves',
  [SwapErrorCode.UnsupportedProvider]: 'error.position.unsupported-provider',
  [SwapErrorCode.SwapNotCreated]: 'error.position.swap-not-created',
  [SwapErrorCode.SwapDataNotFound]: 'error.position.swap-data-not-found',
  [SwapErrorCode.PoolInfoNotFound]: 'error.position.pool-info-not-found',
  [SwapErrorCode.BalanceNotFound]: 'error.position.balance-not-found',
  [SwapErrorCode.BundleNotLanded]: 'error.position.bundle-not-landed',
  [SwapErrorCode.BundleWrongTransaction]:
    'error.position.bundle-wrong-transaction',
  [SwapErrorCode.BundleMissingSignatures]:
    'error.position.bundle-missing-signatures',
  [SwapErrorCode.DexNotSupported]: 'error.position.dex-not-supported',
  [SwapErrorCode.TipsAccountNotFound]: 'error.position.tips-account-not-found',
  [WalletErrorCode.WalletNotFound]: 'error.wallet.wallet-not-found',
  [WalletErrorCode.AssetAlreadyAdded]: 'error.wallet.asset-already-added',
  [WalletErrorCode.WalletAlreadyAdded]: 'error.wallet.wallet-already-added',
  [WalletErrorCode.WalletSignError]: 'error.wallet.wallet-sign-error',
  [WalletErrorCode.InvalidPrivateKey]: 'error.wallet.invalid-private-key',
  [WalletErrorCode.DeviceWallet]: 'error.wallet.device-wallet',
  [WalletErrorCode.KeysUnavailable]: 'error.wallet.keys-unavailable',
  [WalletErrorCode.NoRoute]: 'error.wallet.no-route',
  [WalletErrorCode.RouterUnavailable]: 'error.wallet.router-unavailable',
  [ServicesErrorCode.PoolNotFound]: 'error.services.pool-not-found',
  [ServicesErrorCode.PoolAddressInvalid]: 'error.services.pool-address-invalid',
  [ServicesErrorCode.PoolFetchFailed]: 'error.services.pool-fetch-failed',
  [ServicesErrorCode.PoolsFetchFailed]: 'error.services.pools-fetch-failed',
  [ServicesErrorCode.InfuraNotResponding]:
    'error.services.infura-not-responding',
  [ServicesErrorCode.TokenNotFound]: 'error.services.token-not-found',
  [ServicesErrorCode.PairNotFound]: 'error.services.pair-not-found',
  [ServicesErrorCode.TokenAddressRequired]:
    'error.services.token-address-required',
  [ServicesErrorCode.TokenAddressInvalid]:
    'error.services.token-address-invalid',
  [ServicesErrorCode.NetworkNotSupported]: 'common.something-went-wrong',
  [ServicesErrorCode.PositionNotFound]: 'error.services.position-not-found',
  [ServicesErrorCode.SwapNotFound]: 'error.services.swap-not-found',
  [ServicesErrorCode.NotEnoughLiquidity]: 'error.services.not-enough-liquidity',
  [ServicesErrorCode.TransactionFailed]: 'error.services.transaction-failed',
  [ServicesErrorCode.SignatureRequired]: 'error.services.signature-required',
  [PremiumErrorCode.PremiumNotFound]: 'error.premium.premium-not-found',
  [PremiumErrorCode.NotEnoughPaid]: 'error.premium.not-enough-paid',
  [PremiumErrorCode.TransactionNotFound]: 'error.premium.transaction-not-found',
  [PremiumErrorCode.NoProcessingBoosters]:
    'error.premium.no-processing-boosters',
  [PremiumErrorCode.PremiumAlreadyPurchased]:
    'error.premium.premium-already-purchased',
  [PremiumErrorCode.ImageSafetyCheckFailed]:
    'error.premium.image-safety-check-failed',
  [PremiumErrorCode.ImageContentNotSafe]:
    'error.premium.image-content-not-safe',
  [PremiumErrorCode.ExpireTimeMoreThan7Days]:
    'error.premium.expire-time-more-than-7-days',
  [AuthErrorCode.Unauthorized]: 'error.auth.unauthorized',
  [AuthErrorCode.UserNotFound]: 'error.auth.user-not-found',
  [AuthErrorCode.OTPRequired]: 'error.auth.otp-required',
  [AuthErrorCode.InvalidCredentials]: 'error.auth.invalid-credentials',
  [AuthErrorCode.InvalidPassword]: 'error.auth.invalid-credentials',
  [AuthErrorCode.InvalidToken]: 'error.auth.invalid-token',
  [AuthErrorCode.InviterNotFound]: 'error.auth.inviter-not-found',
  [AuthErrorCode.InvalidOTP]: 'error.auth.invalid-otp',
  [AuthErrorCode.UserAlreadyExists]: 'error.auth.user-already-exists',
  [UserErrorCode.OtpRequired]: 'error.user.otp-required',
  [TradeErrorCode.NoActivePosition]: 'error.trade.no-active-position',
  [TradeErrorCode.NotSupportedProvider]: 'error.trade.not-supported-provider',
  [TradeErrorCode.ApproveInsufficientFunds]:
    'error.trade.approve-insufficient-funds',
  [TradeErrorCode.SwapInsufficientFunds]: 'error.trade.swap-insufficient-funds',
  [TradeErrorCode.UnsupportedNetwork]: 'error.trade.unsupported-network',
  [TradeErrorCode.AllowanceNotFetched]: 'error.trade.allowance-not-fetched',
  [TradeErrorCode.RequiredArgumentMissing]:
    'error.trade.required-argument-missing',
  [TradeErrorCode.TransactionMissing]: 'error.trade.transaction-missing',
  [TradeErrorCode.SwapOrderNotFound]: 'error.trade.swap-order-not-found',
  [TradeErrorCode.TransactionSend]: 'error.trade.transaction-send',
  [TradeErrorCode.InvalidAmount]: 'error.trade.invalid-amount',
  [TradeErrorCode.CreateSwapTransaction]: 'error.trade.create-swap-transaction',
  [TradeErrorCode.Database]: 'error.trade.database',
  [FastifyErrorCode.ContentBodyTooLarge]:
    'error.fastify.content-body-too-large',

  // SecureStorageErrorCode
  [SecureStorageErrorCode.InvalidEncryptionKey]:
    'error.secure-storage.invalid-encryption-key',
  [SecureStorageErrorCode.WrongPassword]: 'error.secure-storage.wrong-password',
  [SecureStorageErrorCode.StorageIsUnlocked]:
    'error.secure-storage.storage-is-unlocked',
  [SecureStorageErrorCode.StorageIsLocked]:
    'error.secure-storage.storage-is-locked',
  [SecureStorageErrorCode.StorageAlreadyInitialized]:
    'error.secure-storage.storage-is-already-initialized',
  [SecureStorageErrorCode.DeriveKeyError]:
    'error.secure-storage.unable-to-derive-key-from-password',
  [SecureStorageErrorCode.DecryptionFailed]:
    'error.secure-storage.decryption-failed',
  [SecureStorageErrorCode.InternalError]: 'error.secure-storage.internal-error',
  [SecureStorageErrorCode.StoreError]: 'error.secure-storage.store-error',
  [SecureStorageErrorCode.StorageError]: 'error.secure-storage.storage-error',
  [SecureStorageErrorCode.AlreadyExistsAddress]:
    'error.secure-storage.already-exists-address',
  [SecureStorageErrorCode.WrongNetwork]: 'error.secure-storage.wrong-network',
  [SecureStorageErrorCode.StorageNotFound]: 'error.secure-storage.not-found',
  [SecureStorageErrorCode.StorageDesynced]: 'error.secure-storage.desynced',
  [SecureStorageErrorCode.StorageNotFoundOrInitialized]:
    'error.secure-storage.not-found',
  [SecureStorageErrorCode.InvalidDelugeId]:
    'error.secure-storage.invalid-deluge-id',
  [SecureStorageErrorCode.SignPermitError]:
    'error.secure-storage.sign-permit-error',
}
